import { yupResolver } from '@hookform/resolvers/yup';
import { Button, IconButton, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useContext, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { notify } from 'components/Notifier';
import { ValidateIcon } from 'components/Icons';
import { postChangePassword } from 'services/apiOperations';
import { AppContext } from 'context';
import MemoEyeCloseIcon from 'components/Icons/EyeCloseIcon';
import MemoEyeOpenIcon from 'components/Icons/EyeOpenIcon';

const useStyles = makeStyles(theme => ({
  title: {
    color: theme.palette.primary.main,
    textTransform: 'uppercase',
    marginBottom: '1.5em',
  },
  form: {
    display: 'flex',
    width: '80%',
    flexFlow: 'column',
    '& > *': {
      marginBottom: '1rem',
    },
    '& > h2': {
      marginTop: 0,
    },
  },
  input: {
    width: '40%',
    marginTop: '2em',
  },
  newPassword: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  submitButtonContainer: {
    width: '100%',
    display: 'flex',
  },
  submitButton: {
    marginLeft: 'auto',
    marginTop: '3.5em',
  },
  eyeButton: {
    width: '50px',
    height: '50px',
    position: 'absolute',
    marginTop: '35px',
    marginLeft: '28%',
  },
  eyeButtonRight: {
    width: '50px',
    height: '50px',
    position: 'absolute',
    marginTop: '35px',
    marginLeft: '75%',
  },
}));

export interface UpdatePasswordFormProps {
  onSuccess: () => void;
}

const UpdatePasswordForm: React.FC<UpdatePasswordFormProps> = ({ onSuccess }) => {
  const classes = useStyles();
  const { t } = useTranslation('translation', { keyPrefix: 'userProfile.parameters.updatePassword' });
  const [showActualPassword, setShowActualPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showNewPasswordConfirmation, setShowNewPasswordConfirmation] = useState(false);
  const context = useContext(AppContext);
  const isEdgeBrowser = context?.isEdgeBrowser?.() ?? false;

  const schema = useMemo(
    () =>
      yup.object().shape({
        oldPassword: yup.string().required(t('validation.requiredPassword')),
        newPassword: yup
          .string()
          .required(t('validation.requiredNewPassword'))
          .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*?])(?=.{8,})/, t('validation.passwordRules')),
        newPasswordConfirmation: yup
          .string()
          .required(t('validation.requiredNewPasswordConfirmation'))
          .oneOf([yup.ref('newPassword'), null], t('validation.confirmationMatch')),
      }),
    [t],
  );

  const { control, handleSubmit, setError } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      newPasswordConfirmation: '',
    },
  });

  const onSubmit = async (values: any) => {
    try {
      await postChangePassword({
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
        newPasswordConfirmation: values.newPasswordConfirmation,
      });
      notify({
        message: t('successMessage'),
        severity: 'success',
      });
      onSuccess();
    } catch (e) {
      setError('oldPassword', {
        type: 'passwordIncorrect',
        message: t('validation.passwordIncorrect'),
      });
    }
  };

  return (
    <>
      <Typography variant="subtitle1" className={classes.title}>
        {t('title')}
      </Typography>
      <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="oldPassword"
          control={control}
          render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
            <>
              <TextField
                InputLabelProps={{ required: true }}
                className={classes.input}
                type={showActualPassword ? 'text' : 'password'}
                label={t('actualPassword')}
                error={!!error}
                helperText={error?.message}
                inputRef={ref}
                {...inputProps}
              />
              {!isEdgeBrowser && (
                <IconButton
                  className={classes.eyeButton}
                  aria-label="toggle password visibility"
                  onClick={() => setShowActualPassword(!showActualPassword)}
                  onMouseDown={event => event.preventDefault()}
                >
                  {showActualPassword ? <MemoEyeCloseIcon /> : <MemoEyeOpenIcon />}
                </IconButton>
              )}
            </>
          )}
        />
        <div className={classes.newPassword}>
          <Controller
            name="newPassword"
            control={control}
            render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
              <>
                <TextField
                  InputLabelProps={{ required: true }}
                  className={classes.input}
                  type={showNewPassword ? 'text' : 'password'}
                  label={t('newPassword')}
                  error={!!error}
                  helperText={error?.message}
                  inputRef={ref}
                  {...inputProps}
                />
                {!isEdgeBrowser && (
                  <IconButton
                    className={classes.eyeButton}
                    aria-label="toggle password visibility"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    onMouseDown={event => event.preventDefault()}
                  >
                    {showNewPassword ? <MemoEyeCloseIcon /> : <MemoEyeOpenIcon />}
                  </IconButton>
                )}
              </>
            )}
          />
          <Controller
            name="newPasswordConfirmation"
            control={control}
            render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
              <>
                <TextField
                  InputLabelProps={{ required: true }}
                  className={classes.input}
                  type={showNewPasswordConfirmation ? 'text' : 'password'}
                  label={t('newPasswordConfirmation')}
                  error={!!error}
                  helperText={error?.message}
                  inputRef={ref}
                  {...inputProps}
                />
                {!isEdgeBrowser && (
                  <IconButton
                    className={classes.eyeButtonRight}
                    aria-label="toggle password visibility"
                    onClick={() => setShowNewPasswordConfirmation(!showNewPasswordConfirmation)}
                    onMouseDown={event => event.preventDefault()}
                  >
                    {showNewPasswordConfirmation ? <MemoEyeCloseIcon /> : <MemoEyeOpenIcon />}
                  </IconButton>
                )}
              </>
            )}
          />
        </div>

        <div className={classes.submitButtonContainer}>
          <Button
            className={classes.submitButton}
            type="submit"
            variant="contained"
            color="primary"
            endIcon={<ValidateIcon />}
          >
            {t('save')}
          </Button>
        </div>
      </form>
    </>
  );
};

export default UpdatePasswordForm;
